/**
 * Locale-Currency Format Mapping
 * Defines how to format prices based on the combination of language locale and currency
 *
 * Format structure:
 * - decimal: decimal separator character
 * - thousand: thousand separator character
 * - symbolPosition: 'before' or 'after' the amount
 * - symbolSpacing: whether to add space between symbol and amount
 */

window.FoxTheme = window.FoxTheme || {};
window.FoxTheme.LocaleCurrencyFormats = {

  /**
   * Default formats by locale (language)
   * These are used as base formats that can be overridden by specific currency rules
   */
  localeDefaults: {
    'fr': { // French
      decimal: ',',
      thousand: ' ',
      symbolPosition: 'after',
      symbolSpacing: true
    },
    'de': { // German
      decimal: ',',
      thousand: '.',
      symbolPosition: 'after',
      symbolSpacing: true
    },
    'es': { // Spanish
      decimal: ',',
      thousand: '.',
      symbolPosition: 'after',
      symbolSpacing: true
    },
    'it': { // Italian
      decimal: ',',
      thousand: '.',
      symbolPosition: 'after',
      symbolSpacing: true
    },
    'vi': { // Vietnamese
      decimal: ',',
      thousand: '.',
      symbolPosition: 'after',
      symbolSpacing: true
    },
    'en': { // English (default)
      decimal: '.',
      thousand: ',',
      symbolPosition: 'before',
      symbolSpacing: false
    }
  },

  /**
   * Specific overrides for locale + currency combinations
   * Format: 'locale-CURRENCY': { format }
   */
  specificFormats: {
    // English with different currencies
    'en-USD': {
      decimal: '.',
      thousand: ',',
      symbolPosition: 'before',
      symbolSpacing: false
    },
    'en-EUR': {
      decimal: '.',
      thousand: ',',
      symbolPosition: 'before',
      symbolSpacing: false
    },
    'en-GBP': {
      decimal: '.',
      thousand: ',',
      symbolPosition: 'before',
      symbolSpacing: false
    },

    // French with different currencies
    'fr-EUR': {
      decimal: ',',
      thousand: ' ',
      symbolPosition: 'after',
      symbolSpacing: true
    },
    'fr-USD': {
      decimal: ',',
      thousand: ' ',
      symbolPosition: 'after',
      symbolSpacing: true
    },
    'fr-CAD': {
      decimal: ',',
      thousand: ' ',
      symbolPosition: 'after',
      symbolSpacing: true
    },

    // German with different currencies
    'de-EUR': {
      decimal: ',',
      thousand: '.',
      symbolPosition: 'after',
      symbolSpacing: true
    },
    'de-CHF': { // Swiss Franc (Switzerland uses apostrophe separator)
      decimal: '.',
      thousand: "'",
      symbolPosition: 'before',
      symbolSpacing: true
    },

    // Spanish with different currencies
    'es-EUR': {
      decimal: ',',
      thousand: '.',
      symbolPosition: 'after',
      symbolSpacing: true
    },
    'es-MXN': { // Mexican Peso
      decimal: '.',
      thousand: ',',
      symbolPosition: 'before',
      symbolSpacing: false
    },

    // Italian with EUR
    'it-EUR': {
      decimal: ',',
      thousand: '.',
      symbolPosition: 'after',
      symbolSpacing: true
    },

    // Vietnamese with VND
    'vi-VND': {
      decimal: ',',
      thousand: '.',
      symbolPosition: 'after',
      symbolSpacing: true
    }
  },

  /**
   * Get the format configuration for a given locale and currency
   * @param {string} locale - Language code (e.g., 'fr', 'en', 'de')
   * @param {string} currency - Currency code (e.g., 'EUR', 'USD', 'GBP')
   * @returns {object} Format configuration
   */
  getFormat: function(locale, currency) {
    // Normalize inputs
    locale = (locale || 'en').toLowerCase().substring(0, 2);
    currency = (currency || 'USD').toUpperCase();

    // Try specific format first (locale-currency combination)
    const specificKey = locale + '-' + currency;
    if (this.specificFormats[specificKey]) {
      return this.specificFormats[specificKey];
    }

    // Fall back to locale default
    if (this.localeDefaults[locale]) {
      return this.localeDefaults[locale];
    }

    // Ultimate fallback to English
    return this.localeDefaults['en'];
  },

  /**
   * Format a price amount according to locale and currency rules
   * @param {number} cents - Amount in cents
   * @param {string} locale - Language code
   * @param {string} currency - Currency code
   * @param {string} currencySymbol - Currency symbol to display
   * @param {boolean} showDecimals - Whether to show decimal places
   * @returns {string} Formatted price string
   */
  formatPrice: function(cents, locale, currency, currencySymbol, showDecimals) {
    const format = this.getFormat(locale, currency);
    const amount = cents / 100;

    // Split into integer and decimal parts
    const amountFixed = showDecimals !== false ? amount.toFixed(2) : Math.floor(amount).toString();
    const parts = amountFixed.split('.');
    let integerPart = parts[0];
    const decimalPart = parts[1];

    // Add thousand separators
    if (format.thousand) {
      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, format.thousand);
    }

    // Combine integer and decimal parts
    let formattedAmount = integerPart;
    if (showDecimals !== false && decimalPart) {
      formattedAmount += format.decimal + decimalPart;
    }

    // Add currency symbol
    const spacing = format.symbolSpacing ? ' ' : '';
    if (format.symbolPosition === 'after') {
      return formattedAmount + spacing + currencySymbol;
    } else {
      return currencySymbol + spacing + formattedAmount;
    }
  }
};
